import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
    AlertCircle,
    Download,
    FileText,
    Loader2,
    Pencil,
    Plus,
    RefreshCw,
    Trash2,
    UploadCloud,
    X,
} from 'lucide-react';
import {
    apiClient,
    DriverDocumentRecord,
    DriverDocumentsOverview,
    DriverDocumentType,
    DriverMedicalCompliance,
} from '../services/api';

interface Props {
    driverId: string;
    canManage: boolean;
    onDriverUpdated?: () => void;
}

interface DocumentEditValue {
    id: string;
    documentType: DriverDocumentType;
    title: string;
    notes: string;
    issuedAt: string;
    expiresAt: string;
}

type RecordTone = 'positive' | 'warning' | 'danger' | 'neutral';

const documentTypeLabels: Record<DriverDocumentType, string> = {
    adr_certificate: 'ADR certificate',
    driver_training: 'Driver training',
    driver_assessment: 'Driver assessment',
    cpc_training_certificate: 'CPC training certificate',
    medical_fitness_evidence: 'Medical fitness evidence',
    grey_fleet_insurance: 'Grey fleet insurance',
    other: 'Other supporting document',
};

const statusStyles: Record<string, string> = {
    current: 'border-green-200 bg-green-100 text-green-800',
    active: 'border-green-200 bg-green-100 text-green-800',
    fit: 'border-green-200 bg-green-100 text-green-800',
    due_soon: 'border-amber-200 bg-amber-100 text-amber-800',
    pending_scan: 'border-blue-200 bg-blue-100 text-blue-800',
    overdue: 'border-red-200 bg-red-100 text-red-800',
    unfit: 'border-red-200 bg-red-100 text-red-800',
    temporarily_unfit: 'border-red-200 bg-red-100 text-red-800',
    rejected: 'border-red-200 bg-red-100 text-red-800',
    unknown: 'border-gray-200 bg-gray-100 text-gray-700',
    not_recorded: 'border-gray-200 bg-gray-100 text-gray-700',
    not_required: 'border-gray-200 bg-gray-100 text-gray-700',
};

const toneDotStyles: Record<RecordTone, string> = {
    positive: 'bg-[#69BE28]',
    warning: 'bg-amber-500',
    danger: 'bg-red-500',
    neutral: 'bg-gray-300',
};

const fieldClass = 'mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#69BE28] focus:ring-2 focus:ring-[#69BE28]/20';
const secondaryButtonClass = 'inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#69BE28]/20';
const primaryButtonClass = 'inline-flex items-center justify-center gap-2 rounded-lg bg-[#69BE28] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#5bab22] focus:outline-none focus:ring-2 focus:ring-[#69BE28]/30 disabled:cursor-not-allowed disabled:opacity-50';
const iconButtonClass = 'rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#69BE28]/20';

const formatDate = (value?: string | null) => value
    ? new Date(`${value.length === 10 ? `${value}T00:00:00` : value}`).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    })
    : 'Not recorded';

const formatStatus = (value?: string | null) => {
    if (!value) return 'Not recorded';
    const normalised = value.replaceAll('_', ' ').toLowerCase();
    return normalised.charAt(0).toUpperCase() + normalised.slice(1);
};

const formatFileSize = (bytes: number) => bytes < 1048576
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1048576).toFixed(1)} MB`;

const getExpiryState = (expiresAt: string | null, warningDays: number): { label: string; style: string } | null => {
    if (!expiresAt) return null;
    const expiry = new Date(`${expiresAt}T00:00:00`).getTime();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (expiry < today.getTime()) return { label: 'Expired', style: statusStyles.overdue };
    if (expiry <= today.getTime() + warningDays * 86400000) return { label: 'Expires soon', style: statusStyles.due_soon };
    return null;
};

const SystemRecord = ({
    title,
    value,
    detail,
    checkedAt,
    tone,
}: {
    title: string;
    value: string;
    detail: string;
    checkedAt?: string | null;
    tone: RecordTone;
}) => (
    <div className="min-w-0 px-4 py-4 md:px-5">
        <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</p>
            <span className={`h-2 w-2 flex-none rounded-full ${toneDotStyles[tone]}`} aria-hidden="true" />
        </div>
        <p className="mt-2 truncate text-sm font-semibold text-gray-900">{formatStatus(value)}</p>
        <p className="mt-1 min-h-8 text-xs leading-4 text-gray-500">{detail}</p>
        <p className="mt-2 text-[11px] font-medium text-gray-400">
            {checkedAt ? `Checked ${formatDate(checkedAt)}` : 'No check available'}
        </p>
    </div>
);

const DocumentEditForm = ({ value, types, saving, onChange, onSave, onCancel }: {
    value: DocumentEditValue;
    types: Array<[DriverDocumentType, string]>;
    saving: boolean;
    onChange: (value: DocumentEditValue) => void;
    onSave: () => void;
    onCancel: () => void;
}) => (
    <div className="bg-gray-50/80 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-xs font-medium text-gray-600">
                Document type
                <select
                    value={value.documentType}
                    onChange={event => onChange({ ...value, documentType: event.target.value as DriverDocumentType })}
                    className={fieldClass}
                >
                    {types.map(([type, label]) => <option key={type} value={type}>{label}</option>)}
                </select>
            </label>
            <label className="text-xs font-medium text-gray-600">
                Title
                <input
                    maxLength={160}
                    value={value.title}
                    onChange={event => onChange({ ...value, title: event.target.value })}
                    className={fieldClass}
                />
            </label>
            <label className="text-xs font-medium text-gray-600">
                Issue date
                <input
                    type="date"
                    value={value.issuedAt}
                    onChange={event => onChange({ ...value, issuedAt: event.target.value })}
                    className={fieldClass}
                />
            </label>
            <label className="text-xs font-medium text-gray-600">
                Expiry date
                <input
                    type="date"
                    min={value.issuedAt || undefined}
                    value={value.expiresAt}
                    onChange={event => onChange({ ...value, expiresAt: event.target.value })}
                    className={fieldClass}
                />
            </label>
            <label className="text-xs font-medium text-gray-600 sm:col-span-2">
                Notes
                <textarea
                    maxLength={2000}
                    rows={2}
                    value={value.notes}
                    onChange={event => onChange({ ...value, notes: event.target.value })}
                    className={`${fieldClass} resize-none`}
                />
            </label>
        </div>
        <div className="mt-4 flex justify-end gap-2 border-t border-gray-200 pt-4">
            <button type="button" onClick={onCancel} className={secondaryButtonClass}>Cancel</button>
            <button type="button" onClick={onSave} disabled={saving || !value.title.trim()} className={primaryButtonClass}>
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save changes
            </button>
        </div>
    </div>
);

const DriverDocumentsTab: React.FC<Props> = ({ driverId, canManage, onDriverUpdated }) => {
    const emptyUpload = {
        type: 'driver_training' as DriverDocumentType,
        title: '',
        notes: '',
        issuedAt: '',
        expiresAt: '',
        file: null as File | null,
    };
    const [overview, setOverview] = useState<DriverDocumentsOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showUpload, setShowUpload] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingMedical, setEditingMedical] = useState(false);
    const [editingDocument, setEditingDocument] = useState<DocumentEditValue | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const [upload, setUpload] = useState(emptyUpload);
    const [medical, setMedical] = useState<Partial<DriverMedicalCompliance>>({});

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await apiClient.getDriverDocumentsOverview(driverId);
            setOverview(data);
            setMedical(data.medicalCompliance);
        } catch (err: any) {
            setError(err.status === 404
                ? 'Document management is not enabled in this environment.'
                : (err.message || 'Unable to load documents'));
        } finally {
            setLoading(false);
        }
    }, [driverId]);

    useEffect(() => { void load(); }, [load]);

    const types = useMemo(() => Object.entries(documentTypeLabels).filter(([type]) => (
        type !== 'medical_fitness_evidence' || overview?.configuration.medicalEvidenceEnabled
    )) as Array<[DriverDocumentType, string]>, [overview]);

    const resetUpload = () => {
        setUpload(emptyUpload);
        if (fileRef.current) fileRef.current.value = '';
        setShowUpload(false);
    };

    const selectFile = (file: File | null) => {
        if (!file || !overview) {
            setUpload(value => ({ ...value, file: null }));
            return;
        }
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Choose a PDF, JPEG or PNG file');
            if (fileRef.current) fileRef.current.value = '';
            setUpload(value => ({ ...value, file: null }));
            return;
        }
        if (file.size > overview.configuration.maxUploadBytes) {
            toast.error(`File must be ${Math.round(overview.configuration.maxUploadBytes / 1048576)} MB or smaller`);
            if (fileRef.current) fileRef.current.value = '';
            setUpload(value => ({ ...value, file: null }));
            return;
        }
        setUpload(value => ({ ...value, file }));
    };

    const submitUpload = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!upload.file || !upload.title.trim()) return;
        setSaving(true);
        try {
            await apiClient.uploadDriverDocument(driverId, {
                file: upload.file,
                documentType: upload.type,
                title: upload.title.trim(),
                notes: upload.notes,
                issuedAt: upload.issuedAt,
                expiresAt: upload.expiresAt,
            });
            toast.success('Document uploaded');
            resetUpload();
            await load();
        } catch (err: any) {
            toast.error(err.message || 'Document upload failed');
        } finally {
            setSaving(false);
        }
    };

    const saveMedical = async () => {
        setSaving(true);
        try {
            const result = await apiClient.updateDriverMedicalCompliance(driverId, {
                assessmentDate: medical.assessmentDate || null,
                reviewDueDate: medical.staffReviewDueDate || null,
                outcome: medical.outcome || 'not_recorded',
                restrictions: medical.restrictions || null,
                evidenceDocumentId: medical.evidenceDocumentId || null,
            });
            setOverview(current => current ? { ...current, medicalCompliance: result } : current);
            setMedical(result);
            setEditingMedical(false);
            toast.success('Medical compliance updated');
            onDriverUpdated?.();
        } catch (err: any) {
            toast.error(err.message || 'Unable to update medical compliance');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (document: DriverDocumentRecord) => {
        if (!window.confirm(`Delete “${document.title}”? This cannot be undone.`)) return;
        try {
            await apiClient.deleteDriverDocument(driverId, document.id);
            toast.success('Document deleted');
            await load();
        } catch (err: any) {
            toast.error(err.message || 'Unable to delete document');
        }
    };

    const saveDocument = async () => {
        if (!editingDocument?.title.trim()) return;
        setSaving(true);
        try {
            await apiClient.updateDriverDocument(driverId, editingDocument.id, {
                documentType: editingDocument.documentType,
                title: editingDocument.title.trim(),
                notes: editingDocument.notes || null,
                issuedAt: editingDocument.issuedAt || null,
                expiresAt: editingDocument.expiresAt || null,
            });
            setEditingDocument(null);
            toast.success('Document details updated');
            await load();
        } catch (err: any) {
            toast.error(err.message || 'Unable to update document');
        } finally {
            setSaving(false);
        }
    };

    if (loading && !overview) {
        return (
            <div className="flex items-center justify-center py-20 text-sm text-gray-500">
                <Loader2 className="mr-2 h-5 w-5 animate-spin text-[#69BE28]" />
                Loading compliance records…
            </div>
        );
    }

    if (!overview) {
        return (
            <div className="p-6">
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    <AlertCircle className="mr-2 inline h-4 w-4" />
                    {error || 'Unable to load documents'}
                    <button type="button" onClick={load} className="ml-3 font-medium text-blue-600 hover:text-blue-800">Try again</button>
                </div>
            </div>
        );
    }

    const { systemRecords, medicalCompliance, documents } = overview;
    const cpcDates = systemRecords.cpc?.records
        .flatMap(record => [record.lgvValidTo, record.pcvValidTo])
        .filter(Boolean)
        .sort() || [];
    const cpcExpiry = cpcDates[cpcDates.length - 1];
    const tachoRecords = systemRecords.tachograph?.records || [];
    const validTachoRecords = tachoRecords.filter(record => record.cardStatus?.toUpperCase() === 'VALID');
    const tacho = [...(validTachoRecords.length ? validTachoRecords : tachoRecords)].sort((a, b) => (
        (b.cardStartOfValidityDate || b.cardExpiryDate || '').localeCompare(a.cardStartOfValidityDate || a.cardExpiryDate || '')
    ))[0];

    const availabilityLabel = (value?: 'available' | 'no_record' | 'not_requested' | 'unavailable') => {
        if (!value) return 'Not checked';
        if (value === 'no_record') return 'No record returned';
        if (value === 'unavailable') return 'Temporarily unavailable';
        if (value === 'not_requested') return 'Not requested';
        return 'Record available';
    };
    const unavailableDetail = (value?: 'available' | 'no_record' | 'not_requested' | 'unavailable') => {
        if (value === 'no_record') return 'DVLA returned no record for this driver';
        if (value === 'unavailable') return 'The latest refresh could not retrieve this record';
        if (value === 'not_requested') return 'Include this record on the next licence check';
        return 'No record available';
    };

    const licenceStatus = systemRecords.licence?.status?.toLowerCase() || '';
    const licenceTone: RecordTone = !systemRecords.licence
        ? 'neutral'
        : ['disqualified', 'revoked', 'expired', 'invalid'].some(value => licenceStatus.includes(value))
            ? 'danger'
            : 'positive';
    const cpcTone: RecordTone = cpcExpiry
        ? 'positive'
        : systemRecords.cpc?.availability === 'unavailable'
            ? 'warning'
            : 'neutral';
    const tachoStatus = tacho?.cardStatus?.toUpperCase();
    const tachoTone: RecordTone = tachoStatus === 'VALID'
        ? 'positive'
        : tacho
            ? 'danger'
            : systemRecords.tachograph?.availability === 'unavailable'
                ? 'warning'
                : 'neutral';
    const medicalEvidenceDocuments = documents.filter(document => (
        document.documentType === 'medical_fitness_evidence' && document.status === 'active'
    ));

    return (
        <div className="space-y-6 p-6">
            {error && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    <span><AlertCircle className="mr-2 inline h-4 w-4" />{error}</span>
                    <button type="button" onClick={load} className="font-medium text-blue-600 hover:text-blue-800">Try again</button>
                </div>
            )}
            <section aria-labelledby="dvla-records-heading">
                <div className="mb-3 flex items-center justify-between gap-4">
                    <div>
                        <h3 id="dvla-records-heading" className="text-xs font-semibold uppercase tracking-wider text-gray-500">DVLA records</h3>
                        <p className="mt-1 text-xs text-gray-500">Latest available check results</p>
                    </div>
                    <button type="button" onClick={load} disabled={loading} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Refresh DVLA records">
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
                <div className="overflow-hidden rounded-lg border border-gray-100 bg-gray-50/80">
                    <div className="divide-y divide-gray-200 md:grid md:grid-cols-3 md:divide-x md:divide-y-0">
                        <SystemRecord
                            title="Driving licence"
                            tone={licenceTone}
                            value={systemRecords.licence?.status || 'Not checked'}
                            detail={systemRecords.licence?.expiryDate
                                ? `Expires ${formatDate(systemRecords.licence.expiryDate)}`
                                : 'No expiry available'}
                            checkedAt={systemRecords.licence?.checkedAt}
                        />
                        <SystemRecord
                            title="Driver CPC"
                            tone={cpcTone}
                            value={availabilityLabel(systemRecords.cpc?.availability)}
                            detail={cpcExpiry
                                ? `Valid to ${formatDate(cpcExpiry)}${systemRecords.cpc?.stale ? ' · refresh pending' : ''}`
                                : unavailableDetail(systemRecords.cpc?.availability)}
                            checkedAt={systemRecords.cpc?.checkedAt || systemRecords.cpc?.lastAttemptAt}
                        />
                        <SystemRecord
                            title="Tachograph card"
                            tone={tachoTone}
                            value={tacho?.cardStatus || availabilityLabel(systemRecords.tachograph?.availability)}
                            detail={tacho?.cardExpiryDate
                                ? `Expires ${formatDate(tacho.cardExpiryDate)}${systemRecords.tachograph?.stale ? ' · refresh pending' : ''}`
                                : unavailableDetail(systemRecords.tachograph?.availability)}
                            checkedAt={systemRecords.tachograph?.checkedAt || systemRecords.tachograph?.lastAttemptAt}
                        />
                    </div>
                </div>
            </section>

            <section aria-labelledby="medical-compliance-heading">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h3 id="medical-compliance-heading" className="text-xs font-semibold uppercase tracking-wider text-gray-500">Medical compliance</h3>
                        <p className="mt-1 text-xs text-gray-500">Record fitness outcomes and review dates only</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${statusStyles[medicalCompliance.status] || statusStyles.unknown}`}>
                            {formatStatus(medicalCompliance.status)}
                        </span>
                        {canManage && (
                            <button
                                type="button"
                                onClick={() => {
                                    if (editingMedical) setMedical(medicalCompliance);
                                    setEditingMedical(value => !value);
                                }}
                                className={editingMedical ? secondaryButtonClass : primaryButtonClass}
                            >
                                {editingMedical ? <><X className="h-4 w-4" />Cancel</> : 'Update'}
                            </button>
                        )}
                    </div>
                </div>

                <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-4">
                    {editingMedical ? (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <label className="text-xs font-medium text-gray-600">
                                Assessment date
                                <input
                                    type="date"
                                    value={medical.assessmentDate || ''}
                                    onChange={event => setMedical(value => ({ ...value, assessmentDate: event.target.value }))}
                                    className={fieldClass}
                                />
                            </label>
                            <label className="text-xs font-medium text-gray-600">
                                Staff review due date
                                <input
                                    type="date"
                                    min={medical.assessmentDate || undefined}
                                    value={medical.staffReviewDueDate || ''}
                                    onChange={event => setMedical(value => ({ ...value, staffReviewDueDate: event.target.value }))}
                                    className={fieldClass}
                                />
                                <span className="mt-1 block text-[11px] font-normal leading-4 text-gray-400">
                                    Overrides the DVLA entitlement date when earlier.
                                </span>
                            </label>
                            <label className="text-xs font-medium text-gray-600">
                                Outcome
                                <select
                                    value={medical.outcome || 'not_recorded'}
                                    onChange={event => setMedical(value => ({ ...value, outcome: event.target.value as DriverMedicalCompliance['outcome'] }))}
                                    className={fieldClass}
                                >
                                    <option value="not_recorded">Not recorded</option>
                                    <option value="fit">Fit</option>
                                    <option value="fit_with_restrictions">Fit with restrictions</option>
                                    <option value="temporarily_unfit">Temporarily unfit</option>
                                </select>
                            </label>
                            <label className="text-xs font-medium text-gray-600">
                                Operational restrictions
                                <input
                                    value={medical.restrictions || ''}
                                    onChange={event => setMedical(value => ({ ...value, restrictions: event.target.value }))}
                                    maxLength={1000}
                                    placeholder="Restrictions only — no clinical details"
                                    className={fieldClass}
                                />
                            </label>
                            {overview.configuration.medicalEvidenceEnabled && (
                                <label className="text-xs font-medium text-gray-600 sm:col-span-2">
                                    Evidence document
                                    <select
                                        value={medical.evidenceDocumentId || ''}
                                        onChange={event => setMedical(value => ({ ...value, evidenceDocumentId: event.target.value || null }))}
                                        className={fieldClass}
                                    >
                                        <option value="">No linked evidence</option>
                                        {medicalEvidenceDocuments.map(document => (
                                            <option key={document.id} value={document.id}>{document.title} · {document.originalFilename}</option>
                                        ))}
                                    </select>
                                    {medicalEvidenceDocuments.length === 0 && (
                                        <span className="mt-1 block text-[11px] font-normal text-gray-400">
                                            Add medical fitness evidence under supporting documents first.
                                        </span>
                                    )}
                                </label>
                            )}
                            <div className="flex justify-end border-t border-gray-200 pt-4 sm:col-span-2">
                                <button type="button" onClick={saveMedical} disabled={saving} className={primaryButtonClass}>
                                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                                    Save medical record
                                </button>
                            </div>
                        </div>
                    ) : (
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-5 text-sm sm:grid-cols-4">
                            <div>
                                <dt className="text-xs text-gray-500">Required</dt>
                                <dd className="mt-1 font-medium text-gray-900">{medicalCompliance.required ? 'Yes — Group 2' : 'No'}</dd>
                            </div>
                            <div>
                                <dt className="text-xs text-gray-500">Assessment</dt>
                                <dd className="mt-1 font-medium text-gray-900">{formatDate(medicalCompliance.assessmentDate)}</dd>
                            </div>
                            <div>
                                <dt className="text-xs text-gray-500">Review due</dt>
                                <dd className="mt-1 font-medium text-gray-900">{formatDate(medicalCompliance.reviewDueDate)}</dd>
                                <p className="mt-0.5 text-[11px] text-gray-400">{formatStatus(medicalCompliance.dueSource || 'No source')}</p>
                            </div>
                            <div>
                                <dt className="text-xs text-gray-500">Outcome</dt>
                                <dd className="mt-1 font-medium text-gray-900">{formatStatus(medicalCompliance.outcome)}</dd>
                            </div>
                        </dl>
                    )}
                </div>
            </section>

            <section aria-labelledby="supporting-documents-heading">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h3 id="supporting-documents-heading" className="text-xs font-semibold uppercase tracking-wider text-gray-500">Supporting documents</h3>
                        <p className="mt-1 text-xs text-gray-500">Certificates and evidence held outside the DVLA check</p>
                    </div>
                    {canManage && (
                        <button
                            type="button"
                            onClick={() => showUpload ? resetUpload() : setShowUpload(true)}
                            className={showUpload ? secondaryButtonClass : primaryButtonClass}
                        >
                            {showUpload ? <><X className="h-4 w-4" />Cancel</> : <><Plus className="h-4 w-4" />Add Document</>}
                        </button>
                    )}
                </div>

                {showUpload && (
                    <form onSubmit={submitUpload} className="mb-4 rounded-lg border border-gray-100 bg-gray-50/80 p-4">
                        <h4 className="mb-4 text-sm font-semibold text-gray-900">New supporting document</h4>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <label className="text-xs font-medium text-gray-600">
                                Document type
                                <select
                                    value={upload.type}
                                    onChange={event => setUpload(value => ({ ...value, type: event.target.value as DriverDocumentType }))}
                                    className={fieldClass}
                                >
                                    {types.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                </select>
                            </label>
                            <label className="text-xs font-medium text-gray-600">
                                Title
                                <input
                                    required
                                    maxLength={160}
                                    value={upload.title}
                                    onChange={event => setUpload(value => ({ ...value, title: event.target.value }))}
                                    placeholder="e.g. Annual driver assessment"
                                    className={fieldClass}
                                />
                            </label>
                            <label className="text-xs font-medium text-gray-600">
                                Issue date
                                <input
                                    type="date"
                                    value={upload.issuedAt}
                                    onChange={event => setUpload(value => ({ ...value, issuedAt: event.target.value }))}
                                    className={fieldClass}
                                />
                            </label>
                            <label className="text-xs font-medium text-gray-600">
                                Expiry date
                                <input
                                    type="date"
                                    min={upload.issuedAt || undefined}
                                    value={upload.expiresAt}
                                    onChange={event => setUpload(value => ({ ...value, expiresAt: event.target.value }))}
                                    className={fieldClass}
                                />
                            </label>
                            <label className="text-xs font-medium text-gray-600 sm:col-span-2">
                                Notes <span className="font-normal text-gray-400">(optional)</span>
                                <textarea
                                    maxLength={2000}
                                    rows={2}
                                    value={upload.notes}
                                    onChange={event => setUpload(value => ({ ...value, notes: event.target.value }))}
                                    placeholder="Add a reference or relevant context"
                                    className={`${fieldClass} resize-none`}
                                />
                            </label>
                            <div className="sm:col-span-2">
                                <p className="text-xs font-medium text-gray-600">File</p>
                                <div className="mt-1 flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 sm:flex-row sm:items-center">
                                    <button
                                        type="button"
                                        onClick={() => fileRef.current?.click()}
                                        className={`${secondaryButtonClass} self-start sm:self-auto`}
                                    >
                                        <UploadCloud className="h-4 w-4" />
                                        Choose file
                                    </button>
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                                        onChange={event => selectFile(event.target.files?.[0] || null)}
                                        className="sr-only"
                                        tabIndex={-1}
                                        aria-hidden="true"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <p className={`truncate text-sm ${upload.file ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
                                            {upload.file ? upload.file.name : 'No file selected'}
                                        </p>
                                        <p className="mt-0.5 text-[11px] text-gray-400">
                                            {upload.file
                                                ? formatFileSize(upload.file.size)
                                                : `PDF, JPEG or PNG · up to ${Math.round(overview.configuration.maxUploadBytes / 1048576)} MB`}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end gap-2 border-t border-gray-200 pt-4">
                            <button type="button" onClick={resetUpload} className={secondaryButtonClass}>Cancel</button>
                            <button disabled={saving || !upload.file || !upload.title.trim()} className={primaryButtonClass}>
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                                {saving ? 'Uploading…' : 'Upload document'}
                            </button>
                        </div>
                    </form>
                )}

                {documents.length === 0 ? (
                    <div className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50/80 p-4">
                        <FileText className="mt-0.5 h-5 w-5 flex-none text-gray-400" />
                        <div>
                            <p className="text-sm font-medium text-gray-900">No supporting documents</p>
                            <p className="mt-1 text-xs text-gray-500">Add certificates or evidence that are not supplied by the DVLA check.</p>
                        </div>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-100 bg-white">
                        {documents.map(document => {
                            const expiryState = getExpiryState(document.expiresAt, overview.configuration.expiryWarningDays);
                            const editTypes = types.some(([type]) => type === document.documentType)
                                ? types
                                : [[document.documentType, documentTypeLabels[document.documentType]], ...types] as Array<[DriverDocumentType, string]>;

                            if (editingDocument?.id === document.id) {
                                return (
                                    <DocumentEditForm
                                        key={document.id}
                                        value={editingDocument}
                                        types={editTypes}
                                        saving={saving}
                                        onChange={setEditingDocument}
                                        onSave={saveDocument}
                                        onCancel={() => setEditingDocument(null)}
                                    />
                                );
                            }

                            return (
                                <div key={document.id} className="flex flex-wrap items-center gap-3 p-4">
                                    <FileText className="h-5 w-5 flex-none text-gray-400" />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <p className="truncate text-sm font-semibold text-gray-900">{document.title}</p>
                                            {document.status !== 'active' && (
                                                <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusStyles[document.status] || statusStyles.unknown}`}>
                                                    {formatStatus(document.status)}
                                                </span>
                                            )}
                                            {expiryState && (
                                                <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${expiryState.style}`}>
                                                    {expiryState.label}
                                                </span>
                                            )}
                                        </div>
                                        <p className="mt-0.5 truncate text-xs text-gray-500">
                                            {documentTypeLabels[document.documentType]} · {document.originalFilename}
                                            {document.expiresAt ? ` · expires ${formatDate(document.expiresAt)}` : ''}
                                        </p>
                                        {document.rejectionReason && <p className="mt-1 text-xs text-red-600">{document.rejectionReason}</p>}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {document.status === 'active' && (
                                            <button
                                                type="button"
                                                onClick={() => apiClient.downloadDriverDocument(driverId, document).catch((err: any) => toast.error(err.message))}
                                                className={iconButtonClass}
                                                aria-label={`Download ${document.title}`}
                                                title="Download"
                                            >
                                                <Download className="h-4 w-4" />
                                            </button>
                                        )}
                                        {canManage && (
                                            <button
                                                type="button"
                                                onClick={() => setEditingDocument({
                                                    id: document.id,
                                                    documentType: document.documentType,
                                                    title: document.title,
                                                    notes: document.notes || '',
                                                    issuedAt: document.issuedAt || '',
                                                    expiresAt: document.expiresAt || '',
                                                })}
                                                className={iconButtonClass}
                                                aria-label={`Edit ${document.title}`}
                                                title="Edit details"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </button>
                                        )}
                                        {canManage && (
                                            <button
                                                type="button"
                                                onClick={() => remove(document)}
                                                className="rounded-lg p-2 text-gray-400 transition hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-200"
                                                aria-label={`Delete ${document.title}`}
                                                title="Delete"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
};

export default DriverDocumentsTab;
